import type { ChatProcess, PlanBuildProcess, TestRunProcess } from "@/lib/chat/chatProcessTypes";

/**
 * What Smithers is doing, as an ordered list of steps for the chat, built
 * only from what the platform recorded: the running turn and each job's
 * ChatProcess (plan builds with their attempts, dry runs with their steps).
 * Smithers cannot add, word or skip a step. Browser-safe.
 * See docs/superpowers/specs/2026-10-19-working-steps-in-chat-design.md.
 */

export type GlyphKind = "reading" | "drafting" | "checking" | "retrying" | "testing" | "writing" | "done" | "failed";
export type StepState = "past" | "current" | "done" | "failed" | "next";

export interface WorkingStep {
  /** Stable across polls, so a step keeps its element and enters only once. */
  key: string;
  kind: GlyphKind;
  state: StepState;
  /** Plain words: no ids, attempt counts or tool names. */
  text: string;
  /** Server time the current step started, for its clock. */
  since?: number;
  /** 0..1, testing only. */
  progress?: number;
}

export type TurnState = "idle" | "thinking" | "replying";

function buildSteps(process: PlanBuildProcess, testFollows: boolean): WorkingStep[] {
  const steps: WorkingStep[] = [];
  const running = process.status === "running";
  const attempts = [...process.attempts].sort((a, b) => a.seq - b.seq);
  // The attempt with the model now is the newest one, when it has not finished: a later attempt
  // proves an earlier unfinished row (a crash) is not running.
  const newest = attempts[attempts.length - 1];
  const live = running && newest && newest.finishedAt === null ? newest : undefined;
  const base = `build:${process.id}`;
  if (running && attempts.length === 0) {
    steps.push({ key: `${base}:start`, kind: "reading", state: "current", text: `Getting ready to plan ${process.workerName}`, since: process.startedAt });
  }
  attempts.forEach((attempt, index) => {
    const first = attempt.seq === 0;
    const finished = attempt.finishedAt !== null;
    const current = attempt === live;
    steps.push({
      key: `${base}:attempt:${attempt.seq}`,
      kind: first ? "drafting" : "retrying",
      state: current ? "current" : "past",
      text: first ? `Drafting the plan for ${process.workerName}` : "Trying again",
      ...(current ? { since: attempt.startedAt } : {}),
    });
    if (finished) {
      const last = index === attempts.length - 1;
      steps.push({
        key: `${base}:check:${attempt.seq}`,
        kind: "checking",
        state: last && running && !live ? "current" : "past",
        text: "Checking the plan",
        ...(last && running && !live && attempt.finishedAt !== null ? { since: attempt.finishedAt } : {}),
      });
    }
  });
  if (process.status === "built") steps.push({ key: `${base}:end`, kind: "done", state: "done", text: "The plan is ready to review" });
  else if (process.status === "failed" || process.status === "error") steps.push({ key: `${base}:end`, kind: "failed", state: "failed", text: "The plan could not be built" });
  // The test asked for follows a running build, or a built one until that test has started.
  if (process.followUpTest && !testFollows && (running || process.status === "built")) {
    steps.push({ key: `${base}:then`, kind: "testing", state: "next", text: "Then: a test run" });
  }
  return steps;
}

function testStep(process: TestRunProcess): WorkingStep {
  const key = `test:${process.id}`;
  if (process.status === "running") {
    const total = Math.max(process.stepsTotal, 1);
    return {
      key, kind: "testing", state: "current",
      text: `Running a test of ${process.workerName}, ${process.stepsDone} of ${process.stepsTotal} steps done`,
      since: process.startedAt,
      progress: Math.min(1, process.stepsDone / total),
    };
  }
  if (process.status === "success") {
    const covered = process.coverage ? ` The tests covered ${process.coverage.tested} of the ${process.coverage.total} steps.` : "";
    return { key, kind: "done", state: "done", text: `Done: the test passed.${covered}` };
  }
  return { key, kind: "failed", state: "failed", text: "The test failed" };
}

export function workingSteps(input: {
  processes: ChatProcess[];
  turn: TurnState;
  turnStartedAt?: number | null;
}): WorkingStep[] {
  const { processes, turn } = input;
  const steps: WorkingStep[] = [];
  // While his reply streams in with no job to show, the text itself says he is answering.
  if (turn === "thinking" || (turn === "replying" && processes.length > 0)) {
    const current = processes.length === 0 && turn === "thinking";
    steps.push({
      // "Working on": the only signal after a reload is that a turn runs, which covers reading and writing alike.
      key: "turn", kind: "reading", state: current ? "current" : "past", text: "Working on your message",
      ...(current && input.turnStartedAt ? { since: input.turnStartedAt } : {}),
    });
  }
  processes.forEach((process, index) => {
    if (process.kind === "plan_build") steps.push(...buildSteps(process, processes.slice(index + 1).some((later) => later.kind === "test_run" && later.workerId === process.workerId)));
    else steps.push(testStep(process));
  });
  // Not while a step waits to start (the test after a rebuild): the reply is not being written yet.
  if (turn === "thinking" && processes.length > 0 && !processes.some((p) => p.status === "running") && !steps.some((step) => step.state === "next")) {
    steps.push({ key: "writing", kind: "writing", state: "current", text: "Writing the reply" });
  }
  return steps;
}
